/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDistanceToNow } from "date-fns";
import { Check, Upload } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  type FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";

import type { BranchesInBrand } from "@/api/admin/brands";
import {
  useGetBrandByIdQuery,
  useGetMenuQuery,
  usePatchMenuMutation,
} from "@/api/admin/brands";
import { getCuisineIdsFromBrandProperties } from "@/api/admin/brands/brandPropertyUtils";
import { CustomButton, CustomCardFooter, Loader } from "@/components/atoms";
import { toastError, toastSuccess } from "@/components/molecules";
import {
  getDefaultFormMenu,
  isMenuReadyToSave,
  mapMenuGetToForm,
} from "@/components/units/BrandFormMenu/menuMappers";
import { rootStore } from "@/store/RootStore";
import { cn } from "@/utils/cn/cn";

import BrandFormMenu from "../BrandFormMenu/BrandFormMenu";

interface MenuPageProps {
  hasUpdatePermission?: boolean;
  branches?: BranchesInBrand[];
  brandId?: string;
}

const AdminMenu: FC<MenuPageProps> = observer(
  ({
    hasUpdatePermission = true,
    branches: branchesProp,
    brandId: brandIdProp,
  }) => {
    const menuStore = rootStore.menuStore;
    const id = useId();

    const [savedIsActive, setSavedIsActive] = useState(false);
    const [savedUpdatedAt, setSavedUpdatedAt] = useState<string | null>(null);

    const { id: routeId } = useParams<{ id: string }>();
    const brandId = brandIdProp ?? routeId ?? "";

    const { data: brandData, isPending: isBrandPending } =
      useGetBrandByIdQuery(brandId);

    const { data: menuData, isPending: isMenuPending } =
      useGetMenuQuery(brandId);

    const { mutateAsync: patchMenu, isPending: isPublishing } =
      usePatchMenuMutation();

    const handleMenuUpdated = useCallback(
      (menu: { isActive?: boolean; updatedAt?: string }) => {
        setSavedIsActive(menu.isActive ?? false);
        setSavedUpdatedAt(menu.updatedAt ?? null);
      },
      []
    );

    const resolvedBranches: BranchesInBrand[] =
      branchesProp ?? brandData?.branches ?? [];

    const formMenu = useMemo(() => {
      const menu = menuData ? mapMenuGetToForm(menuData) : getDefaultFormMenu();
      const cuisine = getCuisineIdsFromBrandProperties(brandData?.properties);

      return {
        ...menu,
        cuisine,
      };
    }, [brandData?.properties, menuData]);

    useEffect(() => {
      if (!menuData) return;

      rootStore.menuStore.initMenu(formMenu);
      setSavedIsActive(menuData.isActive ?? false);
      setSavedUpdatedAt(menuData.updatedAt ?? null);
    }, [formMenu, menuData]);

    useEffect(() => {
      rootStore.menuStore.setBranches(
        resolvedBranches.map(b => ({
          branchId: b.branchId,
          branchName: b.branchName,
          city: b.cityName,
        }))
      );
    }, [resolvedBranches]);

    const handlePublish = async () => {
      if (!brandId) return;
      try {
        const result = await patchMenu({
          brandId,
          menu: {
            isActive: true,
          },
        });
        rootStore.menuStore.initMenu({
          ...mapMenuGetToForm(result),
          cuisine: rootStore.menuStore.menu.cuisine ?? [],
        });
        setSavedIsActive(result.isActive ?? false);
        setSavedUpdatedAt(result.updatedAt ?? null);
        toastSuccess("Menu published to mobile app");
      } catch (error: any) {
        toastError(error.message, () => handlePublish(), "Error");
      }
    };

    const isPublished = savedIsActive;
    const canPublish = isMenuReadyToSave(menuStore.menu);

    const lastPublishedLabel =
      isPublished && savedUpdatedAt
        ? `Last published ${formatDistanceToNow(new Date(savedUpdatedAt), {
            addSuffix: true,
          })}`
        : null;

    if ((isBrandPending && !brandData) || (isMenuPending && !menuData)) {
      return <Loader fullscreen />;
    }

    return (
      <>
        <BrandFormMenu
          defaultValues={formMenu}
          classes="px-4 md:px-8 pt-6"
          branches={resolvedBranches}
          brandId={brandId}
          id={id}
          onMenuUpdated={handleMenuUpdated}
        />

        {hasUpdatePermission && (
          <CustomCardFooter>
            <div className="flex w-full items-center justify-end gap-2">
              {lastPublishedLabel && (
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-1">
                    {isPublished ? (
                      <Check className="h-4 w-4 text-green-700" />
                    ) : (
                      <span className="relative flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-500 opacity-75" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-yellow-500" />
                      </span>
                    )}
                    <span
                      className={cn(
                        "text-base font-bold",
                        isPublished ? "text-green-700" : "text-[#b58c00]"
                      )}
                    >
                      {isPublished ? "Live & up to date" : "Unpublished"}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {lastPublishedLabel}
                  </span>
                </div>
              )}
              <CustomButton
                disabled={!canPublish || isPublishing || isPublished}
                isPending={isPublishing}
                type="button"
                onClick={handlePublish}
                variant="default"
              >
                <Upload className="w-4 h-4" />
                Publish
              </CustomButton>
            </div>
          </CustomCardFooter>
        )}
      </>
    );
  }
);

export default AdminMenu;
